import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { StateService } from '@uirouter/angular';

import { UserService } from './core.user.service';
import { UserRest } from './core.user.rest.service';
import { UiService } from './core.ui.service';
import { ProjectModel } from './project.model.service';
import {
    StateHelperService,
    LOGIN_STATE,
    SITE_HOME_STATE,
    PROJECT_HOME_STATE
} from './core.ui.state.service';
import { GfTranslateService } from './core.translate.service';

export { LOGIN_STATE, SITE_HOME_STATE, PROJECT_HOME_STATE };

/** How long an identical redirect is suppressed, so one denied page load redirects once. */
const REDIRECT_DEDUPE_MS = 2000;

export interface RedirectTarget {
    name: string;
    params: any;
}

export interface DenialOptions {
    /** Project the user was refused; never used as the landing spot. */
    excludeUnixName?: string;
    /** Override the toast body. */
    message?: string;
    /** Override the toast title. */
    title?: string;
    /** Redirect without a toast (the caller already showed one). */
    silent?: boolean;
}

/**
 * Single owner of "the backend refused this, where do we send the user?".
 *
 * - No session      -> `home.user.login`, carrying the current URL as the return URL.
 * - Permission denied -> the current project's homepage when the user can still see it,
 *                        otherwise the site homepage.
 *
 * See docs/PERMISSION-REDIRECT.md. Every caller — the HTTP interceptor, the ui-router
 * error hook and the route resolves — goes through here so the three behave alike.
 */
@Injectable()
export class AccessDeniedService {

    /**
     * The validate call currently in flight, so a burst of parallel 401s fires one
     * request. Everyone who joins gets the same answer rather than assuming the first
     * caller dealt with it — see checkExpiredSession.
     */
    private sessionCheck: Promise<boolean> = null;

    /** Last redirect issued, so a page load that fails N requests still redirects once. */
    private lastRedirect: { key: string, at: number } = null;

    /** Rejection ids already acted on, so defaultErrorHandler does not also log them. */
    private handledRejections: number[] = [];

    constructor(
        private stateService: StateService,
        private userService: UserService,
        private userRest: UserRest,
        private uiService: UiService,
        private projectModel: ProjectModel,
        private stateHelperService: StateHelperService,
        private translateService: GfTranslateService,
    ) {}

    // ------------------------------------------------------------------
    // Targets
    // ------------------------------------------------------------------

    /** @see StateHelperService.buildReturnUrl */
    buildReturnUrl(transition?: any): string {
        return this.stateHelperService.buildReturnUrl(transition);
    }

    loginTarget(transition?: any): RedirectTarget {
        return { name: LOGIN_STATE, params: { url: this.buildReturnUrl(transition) } };
    }

    /**
     * Best landing spot for a user who was refused something.
     *
     * `excludeUnixName` is the project the refusal was *about*: when the project resolve
     * itself is what failed, the current project is either stale or the very thing the
     * user cannot see, so it must not be the destination.
     */
    deniedTarget(excludeUnixName?: string): RedirectTarget {
        const project = this.projectModel.getCurrentProject();
        const unixName = project?.unixName ?? null;

        if (unixName && !this.sameProject(unixName, excludeUnixName) && this.canSeeProject(project)) {
            return { name: PROJECT_HOME_STATE, params: { unixName: unixName } };
        }

        return { name: SITE_HOME_STATE, params: {} };
    }

    /** True if the session user can still open this project's homepage. */
    canSeeProject(project: any): boolean {
        if (!project) return false;
        if (project.isPublic === true || project.isPublic === 'Y') return true;
        if (this.userService.session.isSiteAdmin()) return true;
        return this.hasAnyProjectPermission(project?.rel?.permissions);
    }

    /**
     * True if the permissions map grants *anything* in the project.
     *
     * A numeric (or numeric-string) section value >= 0 is a grant. Booleans and nullish
     * values are never treated as explicit grants.
     */
    hasAnyProjectPermission(permissions: any): boolean {
        if (!permissions || typeof permissions !== 'object') {
            return false;
        }

        return Object.keys(permissions).some((section) => {
            const rawValue = permissions[section];

            if (typeof rawValue === 'number') {
                return rawValue >= 0;
            }

            if (typeof rawValue === 'string') {
                const trimmed = rawValue.trim();
                if (trimmed === '') {
                    return false;
                }
                const numericValue = Number(trimmed);
                if (Number.isNaN(numericValue)) {
                    return false;
                }
                return numericValue >= 0;
            }

            return false;
        });
    }

    private sameProject(unixName: string, other: string): boolean {
        if (!other) return false;
        return String(unixName).toLowerCase() === String(other).toLowerCase();
    }

    // ------------------------------------------------------------------
    // Navigation
    // ------------------------------------------------------------------

    /**
     * Abort the pending transition, then start a new one.
     *
     * In this app's UI-Router setup, throwing a TargetState from a resolve is handled as
     * a transition error (type 6) rather than as a redirect, so we abort and `go`
     * explicitly instead.
     */
    redirect(transition: any, target: RedirectTarget): null {
        // Abort first, and unconditionally: the calling resolve returns null, and a
        // transition left running would carry that null into its child resolves. Only
        // the navigation is deduplicated.
        if (transition && typeof transition.abort === 'function') {
            transition.abort();
        }

        if (this.alreadyRedirectingTo(target)) return null;

        const going: any = this.stateService.go(target.name, target.params, { reload: true });

        // Close the dedupe window as soon as the redirect lands. It exists to collapse a
        // burst of refusals from one page load, and that burst is over long before the
        // new transition settles — whereas a refusal that arrives *after* the user is
        // standing on the target is a fresh one, and suppressing it would abort their
        // navigation with no toast and no visible change.
        if (going && typeof going.then === 'function') {
            const done = () => this.clearRedirect(target);
            going.then(done, done);
        }

        return null;
    }

    /** Send an unauthenticated user to the login screen, carrying the return URL. */
    goLogin(transition?: any): null {
        return this.redirect(transition, this.loginTarget(transition));
    }

    /** Send an authenticated-but-refused user to the best available landing spot. */
    goDenied(transition?: any, options: DenialOptions = {}): null {
        const target = this.deniedTarget(options.excludeUnixName);

        if (!options.silent && !this.alreadyRedirectingTo(target, true)) {
            this.uiService.message.showWarning(
                options.message || this.translateService.translate(
                    'Sorry, you do not have permission to view that page.'
                ),
                options.title || this.translateService.translate('Access Denied')
            );
        }

        return this.redirect(transition, target);
    }

    /**
     * The one entry point worth calling from anywhere: no session sends the user to
     * login with a return URL, a live session sends them to the denial target.
     */
    goRefused(transition?: any, options: DenialOptions = {}): null {
        if (!this.userService.session.hasUser()) {
            return this.goLogin(transition);
        }
        return this.goDenied(transition, options);
    }

    /**
     * Suppress an identical redirect issued within REDIRECT_DEDUPE_MS, so a page whose
     * resolves fire five refused requests still redirects (and toasts) once.
     *
     * `peek` reads the answer without claiming the window, so the toast and the redirect
     * that follows it agree on whether this is the first call.
     */
    private alreadyRedirectingTo(target: RedirectTarget, peek: boolean = false): boolean {
        const key = target.name + '|' + JSON.stringify(target.params || {});
        const now = Date.now();

        if (this.lastRedirect
            && this.lastRedirect.key === key
            && (now - this.lastRedirect.at) < REDIRECT_DEDUPE_MS) {
            return true;
        }

        if (!peek) {
            this.lastRedirect = { key: key, at: now };
        }
        return false;
    }

    /** Drop the dedupe window, unless a different redirect has since claimed it. */
    private clearRedirect(target: RedirectTarget): void {
        const key = target.name + '|' + JSON.stringify(target.params || {});
        if (this.lastRedirect && this.lastRedirect.key === key) {
            this.lastRedirect = null;
        }
    }

    // ------------------------------------------------------------------
    // Session expiry
    // ------------------------------------------------------------------

    /**
     * Confirm with the backend that the session really is gone before acting on a 401,
     * then drop the user at the login screen with a return URL.
     *
     * A single 401 is not proof on its own — it may be one endpoint's own answer — so we
     * ping /api/usersession/{unixName}/validate first.
     */
    checkExpiredSession(transition?: any): Promise<boolean> {
        // Resolves true when the refusal is dealt with — the user was redirected, or is
        // already at the login screen. False means the session turned out to be live and
        // nothing was done, which matters to a caller holding a transition that is now
        // dead, or a stopped poll that nothing else will restart.
        if (this.stateService.current && this.stateService.current.name === LOGIN_STATE) {
            return Promise.resolve(true);
        }

        if (!this.userService.session.hasUser()) {
            this.goLogin(transition);
            return Promise.resolve(true);
        }

        // Join the validate already in flight rather than racing it with a second
        // request — but wait for its *answer*. Reporting `true` here on the assumption
        // that the first caller would handle it is how a refusal goes silent: if that
        // check finds the session healthy it does nothing, and this caller's transition
        // is dead with nobody rehoming the user.
        if (this.sessionCheck) return this.sessionCheck;

        this.sessionCheck = this.userRest.validateSession(this.userService.session.getUnixName())
            .then(
                () => {
                    this.sessionCheck = null;
                    return false;
                },
                () => {
                    this.sessionCheck = null;
                    this.expireSession(transition);
                    return true;
                }
            );

        return this.sessionCheck;
    }

    /** Tear down the local session and send the user to login. */
    expireSession(transition?: any): void {
        if (this.stateService.current && this.stateService.current.name === LOGIN_STATE) return;

        const target = this.loginTarget(transition);
        if (this.alreadyRedirectingTo(target, true)) return;

        this.userService.session.setNobody();
        this.uiService.message.showInfo(
            this.translateService.translate(
                'Your session has expired. Please log in again to continue'
            ),
            this.translateService.translate('Session Expired')
        );

        this.redirect(transition, target);
    }

    // ------------------------------------------------------------------
    // Transition errors
    // ------------------------------------------------------------------

    /**
     * Act on a rejected transition whose rejection carries a 401 or 403.
     *
     * Resolves reject with a `RefusedError`
     *
     * Returns true when it handled the rejection, so the default error handler knows not
     * to also log it.
     */
    handleTransitionError(error: any, transition?: any): boolean {
        if (this.wasHandled(error)) return true;

        // SUPERSEDED / ABORTED / IGNORED are not refusals — and one of them is the abort
        // `redirect()` just issued on the way to the login or denial target.
        const type = error?.type ?? null;
        if (type === 2 || type === 3 || type === 5) return false;

        const status = this.statusOf(error);
        if (status !== 401 && status !== 403) return false;

        this.markHandled(error);

        if (status === 401) {
            // A rejection flagged `sessionExpired` came from code that already validated
            // against the backend (UserService.verify()), so take its word rather than
            // spending a second /validate call re-deriving what it just proved.
            if (_.get(this.detailOf(error), 'sessionExpired', false) === true) {
                this.expireSession(transition);
            } else {
                this.checkExpiredSession(transition).then((handled) => {
                    if (handled) return;

                    // The session is live, so that 401 was the endpoint's own answer
                    // rather than an expiry (§1: it should have been a 403). The
                    // transition is dead either way, and we have already told
                    // defaultErrorHandler not to log it — so land the user somewhere
                    // real instead of on a blank shell.
                    this.goDenied(transition);
                });
            }
        } else {
            this.goRefused(transition);
        }

        return true;
    }

    /** True if handleTransitionError already acted on this rejection. */
    wasHandled(error: any): boolean {
        const id = error?.$id ?? null;
        return id !== null && this.handledRejections.includes(id);
    }

    private markHandled(error: any): void {
        const id = error?.$id ?? null;
        if (id === null) return;
        this.handledRejections.push(id);
        // Ids only need to outlive the rejection they belong to; keep the list bounded.
        if (this.handledRejections.length > 50) {
            this.handledRejections = this.handledRejections.slice(-50);
        }
    }

    /** ui-router wraps a resolve's rejection value in a Rejection; unwrap to the value. */
    private detailOf(error: any): any {
        return _.get(error, 'detail', error);
    }

    /** Pull an HTTP status out of a transition rejection, whatever shape it arrived in. */
    private statusOf(error: any): number {
        const detail = this.detailOf(error);

        // `throw 403` from a resolve arrives as the bare number.
        if (detail === 401 || detail === 403) return detail;

        const raw = detail?.status ?? null;
        if (typeof raw === 'number') return raw;
        if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return parseInt(raw, 10);

        return null;
    }
}
